import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Switch,
  Button,
  message,
  Row,
  Col,
  Select,
} from 'antd';
import api from '@services/api';
import { useTranslation } from 'react-i18next';

type ApnDataType = {
  accounting: number | null;
  apnName: string;
  authentication: number | null;
  createdAt: string;
  createdBy: string;
  disconnect: number | null;
  id: number;
  updatedAt: string | null;
  updatedBy: string | null;
  operator: string | null;
};

interface ApnModalProps {
  visible: boolean;
  onClose: () => void;
  apn?: ApnDataType;
  isEditing: boolean;
  operatorsOptions: { label: string; value: string }[];
}

const ApnModal: React.FC<ApnModalProps> = ({
  visible,
  onClose,
  apn,
  isEditing,
  operatorsOptions,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation('customers');

  useEffect(() => {
    if (apn) {
      form.setFieldsValue({
        apn: apn.apnName,
        flAccounting: apn.accounting === 1,
        flAuthentication: apn.authentication === 1,
        flDisconnect: apn.disconnect === 1,
        operator: apn.operator,
      });
    } else {
      form.resetFields();
    }
  }, [apn, form]);

  const handleFinish = async (values: any) => {
    const transformedValues = {
      ...values,
      flAccounting: values.flAccounting ? 1 : 0,
      flAuthentication: values.flAuthentication ? 1 : 0,
      flDisconnect: values.flDisconnect ? 1 : 0,
      apn: values.apn ? values.apn.toLowerCase() : apn?.apnName,
    };

    try {
      if (isEditing) {
        await api.put(`service-proxy/apn/update/${apn?.id}`, transformedValues);
        message.success(t('apns.msgs.success-updating-apn'));
      } else {
        await api.post('service-proxy/apn', transformedValues);
        message.success(t('apns.msgs.success-creating-apn'));
      }
      onClose();
      form.resetFields();
    } catch (error: any) {
      if (error.response?.status === 409) {
        message.error(t('apns.msgs.error-conflict'));
      } else {
        message.error(t('apns.msgs.error-saving-apn'));
      }
    }
  };

  return (
    <Modal
      open={visible}
      title={isEditing ? t('apns.titles.edit-apn') : t('apns.titles.new-apn')}
      onCancel={() => {
        onClose();
        form.resetFields();
      }}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Row>
          <Col span={24}>
            <Form.Item
              name="apn"
              label={t('apns.apn')}
              rules={[{ required: true, message: t('apns.msgs.enter-apn') }]}
            >
              <Input
                disabled={isEditing}
                onChange={e => {
                  if (!isEditing) {
                    form.setFieldsValue({ apn: e.target.value.toLowerCase() });
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name="operator"
              label={t('apns.operator')}
              rules={[
                { required: true, message: t('apns.msgs.enter-operator') },
              ]}
            >
              <Select showSearch options={operatorsOptions} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="flAccounting"
              label={t('apns.accounting')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="flAuthentication"
              label={t('apns.authentication')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="flDisconnect"
              label={t('apns.disconnect')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="end">
          <Form.Item>
            <Button type="primary" htmlType="submit">
              {t('confirm')}
            </Button>
          </Form.Item>
        </Row>
      </Form>
    </Modal>
  );
};

export default ApnModal;
